// miniMarkdown — a deliberately tiny, constrained markdown renderer for issue
// descriptions. The description composer's toolbar emits only three
// constructs, so this renders exactly those and nothing else:
//   **bold**            → <strong>
//   lines of "- item"   → <ul><li>
//   lines of "1. item"  → <ol><li>
// Everything else is a paragraph; blank lines separate blocks; single line
// breaks within a paragraph become <br>.
//
// It returns React nodes (never raw HTML), so there is no dangerouslySet-
// InnerHTML and no XSS surface — far lighter than pulling in react-markdown +
// a sanitiser for three constructs. Keep this in lockstep with the backend
// renderIssueMarkdown in pdfRenderer.ts so the on-screen thread and the
// exported PDF render a description identically.

import { Fragment, type ReactNode } from "react";

const BULLET = /^\s*-\s+/;
const ORDERED = /^\s*\d+\.\s+/;
const BOLD = /\*\*([^*]+)\*\*/g;

/** Render the inline grammar (only **bold**) into React nodes. */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  BOLD.lastIndex = 0;
  while ((match = BOLD.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    nodes.push(<strong key={`b${key++}`}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export function renderMiniMarkdown(text: string): ReactNode {
  const lines = text.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    if (BULLET.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && BULLET.test(lines[i])) {
        items.push(lines[i].replace(BULLET, ""));
        i++;
      }
      blocks.push(
        <ul key={`ul${key++}`} className="ml-1 list-disc space-y-0.5 pl-4">
          {items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    if (ORDERED.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && ORDERED.test(lines[i])) {
        items.push(lines[i].replace(ORDERED, ""));
        i++;
      }
      blocks.push(
        <ol key={`ol${key++}`} className="ml-1 list-decimal space-y-0.5 pl-4">
          {items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }
    if (lines[i].trim() === "") {
      i++;
      continue;
    }
    // Paragraph: gather consecutive non-list, non-blank lines.
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !BULLET.test(lines[i]) &&
      !ORDERED.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={`p${key++}`} className="whitespace-pre-wrap">
        {para.map((pl, j) => (
          <Fragment key={j}>
            {j > 0 && <br />}
            {renderInline(pl)}
          </Fragment>
        ))}
      </p>,
    );
  }

  return <>{blocks}</>;
}
